/**
 * Commander auras — passive area bonuses projected by heroes/officers to nearby
 * friendly units. Pure functions on TurnWorld, no Phaser dependency.
 *
 * Stacking rule: different effect keys combine independently; the SAME key from
 * multiple carriers takes the MAXIMUM value (not the sum) — see `auraMultiplier`
 * / `auraBonus`.
 */
import type { TurnWorld, Unit, AuraEffect, PlayerId } from './types';
import { UNIT_DEFS } from './units';

export type AuraPayload = AuraEffect['effect'];

function chebyshev(a: { col: number; row: number }, b: { col: number; row: number }): number {
  return Math.max(Math.abs(a.col - b.col), Math.abs(a.row - b.row));
}

/** Aura effect payloads currently affecting `unit` (from its owner's carriers). */
export function activeAurasFor(world: TurnWorld, unit: Unit): AuraPayload[] {
  const effects: AuraPayload[] = [];
  for (const carrier of world.units.values()) {
    if (carrier.owner !== unit.owner) continue;
    const def = UNIT_DEFS[carrier.type];
    if (!def.auras) continue;
    for (const a of def.auras) {
      if (a.target !== 'allies') continue;
      if (chebyshev(carrier, unit) <= a.range) effects.push(a.effect);
    }
  }
  return effects;
}

/** For each friendly unit of `playerId`, the aura payloads affecting it. */
export function computeActiveAuras(world: TurnWorld, playerId: PlayerId): Map<number, AuraPayload[]> {
  const result = new Map<number, AuraPayload[]>();
  for (const unit of world.units.values()) {
    if (unit.owner !== playerId) continue;
    const effects = activeAurasFor(world, unit);
    if (effects.length) result.set(unit.id, effects);
  }
  return result;
}

/** Largest multiplier for `key` across the effects (1.0 when none). */
export function auraMultiplier(effects: AuraPayload[], key: 'attackMultiplier' | 'defenseMultiplier'): number {
  let m = 1;
  for (const e of effects) {
    const v = e[key];
    if (v != null && v > m) m = v;
  }
  return m;
}

/** Largest additive bonus for `key` across the effects (0 when none). */
export function auraBonus(effects: AuraPayload[], key: 'healPerTurn' | 'sightBonus' | 'movementBonus'): number {
  let b = 0;
  for (const e of effects) {
    const v = e[key];
    if (v != null && v > b) b = v;
  }
  return b;
}

export interface AuraHealResult {
  unitId: number;
  healed: number;
}

/**
 * End-of-turn healing from `healPerTurn` auras for `playerId`. Each covered unit
 * below max HP gains the strongest healing aura's amount, capped at max HP.
 */
export function applyAuraHealing(world: TurnWorld, playerId: PlayerId): AuraHealResult[] {
  const results: AuraHealResult[] = [];
  for (const unit of world.units.values()) {
    if (unit.owner !== playerId) continue;
    const def = UNIT_DEFS[unit.type];
    if (unit.hp >= def.maxHp) continue;
    const heal = auraBonus(activeAurasFor(world, unit), 'healPerTurn');
    if (heal <= 0) continue;
    const before = unit.hp;
    unit.hp = Math.min(def.maxHp, unit.hp + heal);
    if (unit.hp > before) results.push({ unitId: unit.id, healed: unit.hp - before });
  }
  return results;
}
